import React, { useState } from 'react';
import {
  PencilIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  StarIcon,
  EllipsisVerticalIcon,
  EyeSlashIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import { TemplateCardProps } from './types';

const TemplateCard: React.FC<TemplateCardProps> = React.memo(({
  template,
  onEdit,
  onDuplicate,
  onDelete,
  onSetDefault,
  onToggleActive,
  saving
}) => {
  const [showActions, setShowActions] = useState(false);

  const templateFields = template.fieldsConfig?.fields || {};
  const visibleFieldsCount = Object.values(templateFields).filter((field: any) => field.visible).length;
  const requiredFieldsCount = Object.values(templateFields).filter((field: any) => field.visible && field.required).length;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div
      className={`group relative bg-white border-2 rounded-lg p-4 transition-all duration-200 hover:shadow-lg hover:-translate-y-1 cursor-pointer min-h-[180px] flex flex-col ${
        template.isActive ? 'border-gray-200 hover:border-blue-400' : 'border-gray-300 bg-gray-50 opacity-75'
      }`}
      onClick={() => template.isActive && onEdit(template)}
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={() => setShowActions(false)}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center space-x-2 mb-1">
            <h3 className="font-semibold text-base text-gray-900 truncate">
              {template.name}
            </h3>
            {template.isDefault && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                <StarIconSolid className="h-3 w-3 mr-1" />
                Default
              </span>
            )}
            {!template.isActive && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                Inactive
              </span>
            )}
          </div>
          {template.description && (
            <p className="text-sm text-gray-600 line-clamp-2">
              {template.description}
            </p>
          )}
        </div>

        {/* Action Menu Button */}
        <div className="relative ml-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setShowActions(!showActions);
            }}
            className={`p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-opacity ${
              showActions ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
            }`}
            aria-label="More actions"
          >
            <EllipsisVerticalIcon className="h-5 w-5" />
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
        <div className="flex items-center space-x-1">
          <EyeIcon className="h-4 w-4" />
          <span>{visibleFieldsCount} visible</span>
        </div>
        <div className="flex items-center space-x-1">
          <span className="text-red-500">*</span>
          <span>{requiredFieldsCount} required</span>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-auto pt-3 border-t border-gray-200">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div>
            {template.creator_first_name && (
              <span>By {template.creator_first_name} {template.creator_last_name}</span>
            )}
          </div>
          <div>
            {formatDate(template.updatedAt)}
          </div>
        </div>
      </div>

      {/* Hover Actions */}
      <div
        className={`absolute bottom-4 right-4 flex items-center space-x-1 transition-opacity ${
          showActions ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {!template.isDefault && template.isActive && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onSetDefault(template);
            }}
            disabled={saving}
            className="p-2 rounded-md text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 transition-colors disabled:opacity-50"
            title="Set as default"
            aria-label="Set as default"
          >
            <StarIcon className="h-4 w-4" />
          </button>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate(template);
          }}
          disabled={saving}
          className="p-2 rounded-md text-gray-500 hover:text-purple-600 hover:bg-purple-50 transition-colors disabled:opacity-50"
          title="Duplicate template"
          aria-label="Duplicate template"
        >
          <DocumentDuplicateIcon className="h-4 w-4" />
        </button>

        {template.isActive && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onEdit(template);
            }}
            disabled={saving}
            className="p-2 rounded-md text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
            title="Edit template"
            aria-label="Edit template"
          >
            <PencilIcon className="h-4 w-4" />
          </button>
        )}

        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggleActive(template);
          }}
          disabled={saving}
          className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors disabled:opacity-50"
          title={template.isActive ? 'Deactivate' : 'Activate'}
          aria-label={template.isActive ? 'Deactivate' : 'Activate'}
        >
          {template.isActive ? (
            <EyeSlashIcon className="h-4 w-4" />
          ) : (
            <EyeIcon className="h-4 w-4" />
          )}
        </button>

        {!template.isDefault && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(template);
            }}
            disabled={saving}
            className="p-2 rounded-md text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
            title="Delete template"
            aria-label="Delete template"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.template.id === nextProps.template.id &&
    prevProps.template.updatedAt === nextProps.template.updatedAt &&
    prevProps.template.isActive === nextProps.template.isActive &&
    prevProps.template.isDefault === nextProps.template.isDefault &&
    prevProps.saving === nextProps.saving
  );
});

TemplateCard.displayName = 'TemplateCard';

export default TemplateCard;
